import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

export interface FilterSessionContext {
  routePath?: string;
  userId: string;
  companyId: number;
  branchId: number;
}

export interface FilterSessionState {
  routePath: string;
  userId: string;
  companyId: number;
  branchId: number;
  updatedAt: string;
  data: Record<string, any>;
}

@Injectable({
  providedIn: 'root'
})
export class FilterSessionService {
  private readonly storageKey = 'Page_Filters';
  private readonly storage = sessionStorage;

  constructor(private router: Router) {
    const legacy = localStorage.getItem(this.storageKey);
    if (legacy && !this.storage.getItem(this.storageKey)) {
      this.storage.setItem(this.storageKey, legacy);
    }
    localStorage.removeItem(this.storageKey);
  }

  save(context: FilterSessionContext, data: Record<string, any>): void {
    const routePath = this.normalizeRoutePath(this.resolveRoutePath(context.routePath));
    const payload: FilterSessionState = {
      routePath,
      userId: context.userId,
      companyId: context.companyId,
      branchId: context.branchId,
      updatedAt: new Date().toISOString(),
      data: this.pruneUndefined(data)
    };

    const map = this.readMap();
    const mapKey = this.buildKey({ ...context, routePath });
    map[mapKey] = payload;
    this.writeMap(map);
  }

  load(context: FilterSessionContext): Record<string, any> | null {
    const routePath = this.normalizeRoutePath(this.resolveRoutePath(context.routePath));
    const map = this.readMap();
    const mapKey = this.buildKey({ ...context, routePath });
    const entry = map[mapKey];

    if (!entry || entry.routePath !== routePath) {
      return null;
    }

    return entry.data || null;
  }

  clear(context: FilterSessionContext): void {
    const routePath = this.normalizeRoutePath(this.resolveRoutePath(context.routePath));
    const map = this.readMap();
    const mapKey = this.buildKey({ ...context, routePath });
    if (map[mapKey]) {
      delete map[mapKey];
      this.writeMap(map);
    }
  }

  clearByRoute(routePath: string): void {
    const normalized = this.normalizeRoutePath(routePath);
    const map = this.readMap();
    let changed = false;

    Object.keys(map).forEach(key => {
      if (key.startsWith(`${normalized}::`)) {
        delete map[key];
        changed = true;
      }
    });

    if (changed) {
      this.writeMap(map);
    }
  }

  private buildKey(context: FilterSessionContext): string {
    return `${context.routePath}::${context.companyId}::${context.branchId}::${context.userId}`;
  }

  private normalizeRoutePath(routePath: string): string {
    const path = routePath.split('?')[0].trim();
    if (!path.startsWith('/')) {
      return `/${path}`;
    }
    return path;
  }

  private resolveRoutePath(routePath?: string): string {
    if (routePath && routePath.trim()) {
      return routePath;
    }

    return this.router.url.split('?')[0];
  }

  private pruneUndefined(data: Record<string, any>): Record<string, any> {
    const entries = Object.entries(data).filter(([, value]) => value !== undefined);
    return Object.fromEntries(entries);
  }

  private readMap(): Record<string, FilterSessionState> {
    const raw = this.storage.getItem(this.storageKey);
    if (!raw) {
      return {};
    }

    try {
      const parsed = JSON.parse(raw) as Record<string, FilterSessionState>;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      this.storage.removeItem(this.storageKey);
      return {};
    }
  }

  private writeMap(map: Record<string, FilterSessionState>): void {
    this.storage.setItem(this.storageKey, JSON.stringify(map));
  }
}
